import {
  AmountDimension,
  AmountLimitsDimension,
  BooleanDimension,
  CalendarDateDimension,
  ExclusionDimension,
  FinancialRateDimension,
  FinancialRateWithUnsetDimension,
  frPrelSelectedDimension,
  StringsArrayDimension,
  StringsEnumDimension,
  StringsWithDataEnumDimension,
} from '@orus.eu/dimensions'
import {
  frPrelPracticedSportEntries,
  frPrelPracticedSportNoneValue,
  frPrelProfessionalStatusEntries,
  frPrelMandatorySupplementaryEntries,
  frPrelProductVersions,
  type FrPrelPracticedSport,
} from '@orus.eu/product'

export const frPrelExclusionsDimension = new ExclusionDimension({
  name: 'frPrelExclusions',
  displayKeys: { name: 'dimension_name_fr_prel_exclusions' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: false },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const frPrelPreQuoteExclusionsDimension = new ExclusionDimension({
  name: 'frPrelPreQuoteExclusions',
  displayKeys: { name: 'dimension_name_fr_prel_pre_quote_exclusions' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: false },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const frPrelPostQuoteExclusionsDimension = new ExclusionDimension({
  name: 'frPrelPostQuoteExclusions',
  displayKeys: { name: 'dimension_name_fr_prel_post_quote_exclusions' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: false },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const frPrelProductVersionDimension = new StringsEnumDimension({
  name: 'frPrelProductVersion',
  displayKeys: { name: 'dimension_name_fr_prel_product_version' },
  values: frPrelProductVersions,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const frPrelProfessionalStatusDimension = new StringsWithDataEnumDimension({
  name: 'frPrelProfessionalStatus',
  displayKeys: { name: 'dimension_name_fr_prel_professional_status' },
  entries: frPrelProfessionalStatusEntries,
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: {
    lockedInQuoteFor: {
      client: frPrelSelectedDimension,
    },
  },
} as const)

export const frPrelIsMandataireSocialDimension = new BooleanDimension({
  name: 'frPrelIsMandataireSocial',
  displayKeys: { name: 'dimension_name_fr_prel_is_mandataire_social' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mandatorySupplementaryDimension = new StringsWithDataEnumDimension({
  name: 'mandatorySupplementary',
  displayKeys: {
    name: 'dimension_name_mandatory_supplementary',
    hint: 'dimension_hint_mandatory_supplementary',
  },
  entries: frPrelMandatorySupplementaryEntries,
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: {
    lockedInQuoteFor: {
      client: frPrelSelectedDimension,
    },
  },
} as const)

export const frPrelIsCompanyCreatorOrBuyerDimension = new BooleanDimension({
  name: 'frPrelIsCompanyCreatorOrBuyer',
  displayKeys: { name: 'dimension_name_fr_prel_is_company_creator_or_buyer' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: {
    lockedInQuoteFor: {
      client: frPrelSelectedDimension,
    },
  },
} as const)

export const frPrelIsDrivingMoreThan30000KmPerYearDimension = new BooleanDimension({
  name: 'frPrelIsDrivingMoreThan30000KmPerYear',
  displayKeys: { name: 'dimension_name_fr_prel_is_driving_more_than_30000_km_per_year' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: {
    lockedInQuoteFor: {
      client: frPrelSelectedDimension,
    },
  },
} as const)

const CLAIM_IN_PROGRESS = { frPrelDeathBenefitClaimInProgress: true } as const

/**
 * While a claim runs the capital is nobody's to move, `platform` included: a CPMS payment is applied by
 * `frPrelLumpSumPaymentJob`, which reads the capital it subtracts from off the live contract, so a Care
 * hand-edit in the same window would be counted twice. Closing the claim from the back-office contract page is
 * the sanctioned way to unlock it. A system write passes `userType: undefined` and is not subject to the
 * check, which is how the job moves it.
 */
export const frPrelDeathBenefitTotalDimension = new AmountDimension({
  name: 'frPrelDeathBenefitTotal',
  displayKeys: { name: 'dimension_name_fr_prel_death_benefit_total' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: {
    lockedInQuoteFor: {
      client: frPrelSelectedDimension,
    },
    lockedInEndorsementFor: {
      client: CLAIM_IN_PROGRESS,
      partner: CLAIM_IN_PROGRESS,
      platform: CLAIM_IN_PROGRESS,
    },
    lockedInRenewalFor: {
      client: CLAIM_IN_PROGRESS,
      partner: CLAIM_IN_PROGRESS,
      platform: CLAIM_IN_PROGRESS,
    },
  },
} as const)

export const frPrelDeathBenefitTotalLimitsDimension = new AmountLimitsDimension({
  name: 'frPrelDeathBenefitTotalLimits',
  displayKeys: { name: 'dimension_name_fr_prel_death_benefit_total_limits' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const frPrelIncapacityDisabilityDimension = new AmountDimension({
  name: 'frPrelIncapacityDisability',
  displayKeys: { name: 'dimension_name_fr_prel_incapacity_disability' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const frPrelIncapacityDisabilityLimitsDimension = new AmountLimitsDimension({
  name: 'frPrelIncapacityDisabilityLimits',
  displayKeys: { name: 'dimension_name_fr_prel_incapacity_disability_limits' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const frPrelRecurringGeneralExpenseTotalDimension = new AmountDimension({
  name: 'frPrelRecurringGeneralExpenseTotal',
  displayKeys: { name: 'dimension_name_fr_prel_recurring_general_expense_total' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const frPrelRecurringGeneralExpenseTotalLimitsDimension = new AmountLimitsDimension({
  name: 'frPrelRecurringGeneralExpenseTotalLimits',
  displayKeys: { name: 'dimension_name_fr_prel_recurring_general_expense_total_limits' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const frPrelIsWantsToDeductDeathBenefitTotalContributionsFromTaxableIncomeRequiredDimension =
  new BooleanDimension({
    name: 'frPrelIsWantsToDeductDeathBenefitTotalContributionsFromTaxableIncomeRequiredDimension',
    displayKeys: {
      name: 'dimension_name_fr_prel_wants_to_deduct_death_benefit_total_contributions_from_taxable_income_required',
    },
    considerForDifferenceComputationInUi: { client: false, partner: false, platform: false },
    includeInTrackingMessages: false,
    inputLocking: undefined,
  } as const)

export const frPrelWantsToDeductDeathBenefitTotalContributionsFromTaxableIncomeDimension = new BooleanDimension({
  name: 'frPrelWantsToDeductDeathBenefitTotalContributionsFromTaxableIncome',
  displayKeys: {
    name: 'dimension_name_fr_prel_wants_to_deduct_death_benefit_total_contributions_from_taxable_income',
  },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: {
    lockedInQuoteFor: {
      client: frPrelSelectedDimension,
    },
  },
} as const)

export const frPrelIsWantsToDeductTotalTemporaryIncapacityWorkContributionsFromTaxableIncomeRequiredDimension =
  new BooleanDimension({
    name: 'frPrelIsWantsToDeductTotalTemporaryIncapacityWorkContributionsFromTaxableIncomeRequiredDimension',
    displayKeys: {
      name: 'dimension_name_fr_prel_wants_to_deduct_total_temporary_incapacity_work_contributions_from_taxable_income_required',
    },
    considerForDifferenceComputationInUi: { client: false, partner: false, platform: false },
    includeInTrackingMessages: false,
    inputLocking: undefined,
  } as const)

export const frPrelWantsToDeductTotalTemporaryIncapacityWorkContributionsFromTaxableIncomeDimension =
  new BooleanDimension({
    name: 'frPrelWantsToDeductTotalTemporaryIncapacityWorkContributionsFromTaxableIncome',
    displayKeys: {
      name: 'dimension_name_fr_prel_wants_to_deduct_total_temporary_incapacity_work_contributions_from_taxable_income',
    },
    considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
    includeInTrackingMessages: false,
    inputLocking: {
      lockedInQuoteFor: {
        client: frPrelSelectedDimension,
      },
    },
  } as const)

export const frPrelIsWantsToDeductSpousePensionContributionsFromTaxableIncomeRequiredDimension = new BooleanDimension({
  name: 'frPrelIsWantsToDeductSpousePensionContributionsFromTaxableIncomeRequiredDimension',
  displayKeys: {
    name: 'dimension_name_fr_prel_wants_to_deduct_spouse_pension_contributions_from_taxable_income_required',
  },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: false },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const frPrelWantsToDeductSpousePensionContributionsFromTaxableIncomeDimension = new BooleanDimension({
  name: 'frPrelWantsToDeductSpousePensionContributionsFromTaxableIncome',
  displayKeys: {
    name: 'dimension_name_fr_prel_wants_to_deduct_spouse_pension_contributions_from_taxable_income',
  },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: {
    lockedInQuoteFor: {
      client: frPrelSelectedDimension,
    },
  },
} as const)

export const frPrelIsWantsToDeductEducationAnnuityContributionsFromTaxableIncomeRequired = new BooleanDimension({
  name: 'frPrelIsWantsToDeductEducationAnnuityContributionsFromTaxableIncomeRequiredDimension',
  displayKeys: {
    name: 'dimension_name_fr_prel_wants_to_deduct_education_annuity_contributions_from_taxable_income_required',
  },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: false },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const frPrelWantsToDeductEducationAnnuityContributionsFromTaxableIncomeDimension = new BooleanDimension({
  name: 'frPrelWantsToDeductEducationAnnuityContributionsFromTaxableIncome',
  displayKeys: {
    name: 'dimension_name_fr_prel_wants_to_deduct_education_annuity_contributions_from_taxable_income',
  },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: {
    lockedInQuoteFor: {
      client: frPrelSelectedDimension,
    },
  },
} as const)

export const frPrelPracticedSportDimension = new StringsArrayDimension({
  name: 'frPrelPracticedSport',
  displayKeys: { name: 'dimension_name_fr_prel_practiced_sport' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: true,
  inputLocking: {
    lockedInQuoteFor: {
      client: frPrelSelectedDimension,
    },
  },
  allowedValues: frPrelPracticedSportEntries.map(([key]) => key),
  exclusiveValue: frPrelPracticedSportNoneValue,
  labels: frPrelPracticedSportEntries.reduce(
    (acc, [key, value]) => {
      acc[key as FrPrelPracticedSport] = value.label
      return acc
    },
    {} as Record<FrPrelPracticedSport, string>,
  ),
} as const)

/**
 * The five health questionnaire dimensions below stay out of the tracking messages on purpose: they used to ride in
 * `subscription_updated`, which fires on any subscription change, so the CRM could not tell a prospect filling the
 * questionnaire from someone editing an unrelated field. They are reported by `fr_prel_health_questionnaire_updated`
 * instead, which is sent only when one of these answers changes (INS-1739).
 */
export const frPrelIsHealthSimplifiedQuestionsAnswersValidDimension = new BooleanDimension({
  name: 'frPrelIsHealthSimplifiedQuestionsAnswersValid',
  displayKeys: {
    name: 'dimension_name_fr_prel_is_health_simplified_questions_answers_valid',
  },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: {
    lockedInQuoteFor: {
      client: frPrelSelectedDimension,
    },
  },
} as const)

export const frPrelIsHealthHistoryAndQuestionsMandatoryDimension = new BooleanDimension({
  name: 'frPrelIsHealthHistoryAndQuestionsMandatory',
  displayKeys: {
    name: 'dimension_name_fr_prel_is_health_history_and_questions_mandatory',
  },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: {
    lockedInQuoteFor: {
      client: frPrelSelectedDimension,
    },
  },
} as const)

export const frPrelIsHealthSimplifiedQuestionsAnswersExclusionaryDimension = new BooleanDimension({
  name: 'frPrelIsHealthSimplifiedQuestionsAnswersExclusionary',
  displayKeys: {
    name: 'dimension_name_fr_prel_is_health_simplified_questions_answers_exclusionary',
  },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: {
    lockedInQuoteFor: {
      client: frPrelSelectedDimension,
    },
  },
} as const)

export const frPrelIsHealthHistoryQuestionsAnswersValidDimension = new BooleanDimension({
  name: 'frPrelIsHealthHistoryQuestionsAnswersValid',
  displayKeys: {
    name: 'dimension_name_fr_prel_is_health_history_questions_answers_valid',
  },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: {
    lockedInQuoteFor: {
      client: frPrelSelectedDimension,
    },
  },
} as const)

export const frPrelIsHealthQuestionsAnswersValidDimension = new BooleanDimension({
  name: 'frPrelIsHealthQuestionsAnswersValid',
  displayKeys: {
    name: 'dimension_name_fr_prel_is_health_questions_answers_valid',
  },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: {
    lockedInQuoteFor: {
      client: frPrelSelectedDimension,
    },
  },
} as const)

export const doesAbroadTravelExemptEUDimension = new BooleanDimension({
  name: 'doesAbroadTravelExemptEU',
  displayKeys: {
    name: 'dimension_name_fr_prel_does_abroad_travel_exempt_eu',
  },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: {
    lockedInQuoteFor: {
      client: frPrelSelectedDimension,
    },
  },
} as const)

export const frPrelInstallmentFeeRateDimension = new FinancialRateDimension({
  name: 'frPrelInstallmentFeeRate',
  displayKeys: { name: 'dimension_name_fr_prel_installment_fee_rate' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: {
    lockedInQuoteFor: {
      client: frPrelSelectedDimension,
    },
  },
} as const)

export const defaultFrPrelInstallmentFeeRateDimension = new FinancialRateDimension({
  name: 'defaultFrPrelInstallmentFeeRate',
  displayKeys: { name: 'dimension_name_default_fr_prel_installment_fee_rate' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: {
    lockedInQuoteFor: {
      client: frPrelSelectedDimension,
    },
  },
} as const)

export const manuallySetFrPrelInstallmentFeeRateDimension = new FinancialRateWithUnsetDimension({
  name: 'manuallySetFrPrelInstallmentFeeRate',
  displayKeys: { name: 'dimension_name_manually_set_fr_prel_installment_fee_rate' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: {
    lockedInQuoteFor: {
      client: frPrelSelectedDimension,
    },
  },
} as const)

export const frPrelManagementFeeRateDimension = new FinancialRateDimension({
  name: 'frPrelManagementFeeRate',
  displayKeys: { name: 'dimension_name_fr_prel_management_fee_rate' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: {
    lockedInQuoteFor: {
      client: frPrelSelectedDimension,
    },
  },
} as const)

export const defaultFrPrelManagementFeeRateDimension = new FinancialRateDimension({
  name: 'defaultFrPrelManagementFeeRate',
  displayKeys: { name: 'dimension_name_default_fr_prel_management_fee_rate' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: {
    lockedInQuoteFor: {
      client: frPrelSelectedDimension,
    },
  },
} as const)

export const manuallySetFrPrelManagementFeeRateDimension = new FinancialRateWithUnsetDimension({
  name: 'manuallySetFrPrelManagementFeeRate',
  displayKeys: { name: 'dimension_name_manually_set_fr_prel_management_fee_rate' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: {
    lockedInQuoteFor: {
      client: frPrelSelectedDimension,
    },
  },
} as const)

export const frPrelHasMadelinCertificateDimension = new BooleanDimension({
  name: 'frPrelHasMadelinCertificate',
  displayKeys: {
    name: 'dimension_name_fr_prel_has_madelin_certificate',
  },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: true,
  inputLocking: {
    lockedInQuoteFor: {
      client: frPrelSelectedDimension,
    },
  },
} as const)

export const frPrelHasFgpCertificateDimension = new BooleanDimension({
  name: 'frPrelHasFgpCertificate',
  displayKeys: {
    name: 'dimension_name_fr_prel_has_fgp_certificate',
  },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: {
    lockedInQuoteFor: {
      client: frPrelSelectedDimension,
    },
  },
} as const)

export const frPrelMinBirthDateDimension = new CalendarDateDimension({
  name: 'frPrelMinBirthDate',
  displayKeys: { name: 'dimension_name_fr_prel_min_birth_date' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const frPrelMaxBirthDateDimension = new CalendarDateDimension({
  name: 'frPrelMaxBirthDate',
  displayKeys: { name: 'dimension_name_fr_prel_max_birth_date' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
